import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { authService } from '../services/authService';
import {
  ChangePasswordRequest,
  ForgotPasswordRequest,
  LoginRequest,
  OAuthLoginRequest,
  RegisterRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  User,
} from '../types/auth';
import { AuthContextType, AuthProviderProps } from '../types/contexts';
import { JwtUtils } from '../utils/jwt';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Compute admin status from token
  const isAdmin = token ? JwtUtils.isAdminFromToken(token) : false;
  const isSuperAdmin = token ? JwtUtils.isSuperAdminFromToken(token) : false;
  const userRole = token
    ? JwtUtils.getRoleFromToken(token) || 'Customer'
    : 'Customer';

  const logout = useCallback(() => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    authService.removeAuthToken();
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      if (!token) return;

      const updatedUser = await authService.getProfile();
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      throw error;
    }
  }, [token]);

  useEffect(() => {
    // Check for existing auth data on mount
    const storedToken = localStorage.getItem('authToken');
    const storedUser = localStorage.getItem('user');

    if (storedToken && storedUser) {
      try {
        // Check if token is expired
        if (JwtUtils.isTokenExpired(storedToken)) {
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          setIsLoading(false);
          return;
        }

        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        authService.setAuthToken(storedToken);

        // Refresh profile to get latest data
        refreshProfile().catch(() => {
          // If refresh fails, user might be invalid - logout
          logout();
        });
      } catch (error) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
      }
    }

    setIsLoading(false);
  }, [logout, refreshProfile]);

  const login = async (credentials: LoginRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.login(credentials);
      const { token: authToken, user: userData } = response;

      setToken(authToken);
      setUser(userData);

      // Always store auth cookies required for security (necessary)
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(userData));

      authService.setAuthToken(authToken);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: RegisterRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.register(userData);
      const { token: authToken, user: newUser } = response;

      setToken(authToken);
      setUser(newUser);

      // Always store auth cookies required for security (necessary)
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(newUser));

      authService.setAuthToken(authToken);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const oauthLogin = async (oauthData: OAuthLoginRequest) => {
    try {
      setIsLoading(true);
      const response = await authService.oauthLogin(oauthData);
      const { token: authToken, user: userData } = response;

      setToken(authToken);
      setUser(userData);

      // Always store auth cookies required for security (necessary)
      localStorage.setItem('authToken', authToken);
      localStorage.setItem('user', JSON.stringify(userData));

      authService.setAuthToken(authToken);
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileData: UpdateProfileRequest) => {
    try {
      const updatedUser = await authService.updateProfile(profileData);
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      throw error;
    }
  };

  const changePassword = async (passwordData: ChangePasswordRequest) => {
    try {
      await authService.changePassword(passwordData);
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (emailData: ForgotPasswordRequest) => {
    try {
      await authService.forgotPassword(emailData);
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (resetData: ResetPasswordRequest) => {
    try {
      await authService.resetPassword(resetData);
    } catch (error) {
      throw error;
    }
  };

  const linkOAuthProvider = async (provider: string, accessToken: string) => {
    try {
      await authService.linkOAuthProvider(provider, accessToken);
      await refreshProfile(); // Refresh to get updated OAuth providers
    } catch (error) {
      throw error;
    }
  };

  const unlinkOAuthProvider = async (provider: string) => {
    try {
      await authService.unlinkOAuthProvider(provider);
      await refreshProfile(); // Refresh to get updated OAuth providers
    } catch (error) {
      throw error;
    }
  };

  const confirmEmail = async (token: string, email?: string) => {
    try {
      await authService.confirmEmail(token, email);
      await refreshProfile(); // Refresh to get updated email confirmation status
    } catch (error) {
      throw error;
    }
  };

  const resendConfirmationEmail = async () => {
    try {
      await authService.resendConfirmationEmail();
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    token,
    isAuthenticated: !!user && !!token,
    isLoading,
    isAdmin,
    isSuperAdmin,
    userRole,
    login,
    register,
    oauthLogin,
    logout,
    updateProfile,
    changePassword,
    forgotPassword,
    resetPassword,
    linkOAuthProvider,
    unlinkOAuthProvider,
    confirmEmail,
    resendConfirmationEmail,
    refreshProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
